// ** React Imports
import React, { useState, useEffect } from 'react';

// ** MUI Imports
import { Box, useTheme, Card, useMediaQuery, CardActions, Grid, Button, Divider, Typography, Checkbox, CardContent, FormControlLabel, FormControl, MenuItem, InputLabel, Select, Tooltip } from '@mui/material';
import 'react-datepicker/dist/react-datepicker.css';
import Title from '../../../components/Title';
import axios from "axios";
import { Office } from '../../../models/office';
import { Collect } from '../../../models/collect';
import useAxiosInterceptor from '../../AxiosInterceptor';
import { useDrawer } from '../../../components/context/DrawerContext';
import { stickyListRegionWidth } from '../../../components/layouts/StickyListToolbar';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import { SimpleColumn, SimpleRow, RowId } from '../../../components/table/interface';
import { Save as SaveIcon, InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import TimePickerSelect from '../../../components/TimePickerSelect';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const ActualizarTerminalAdd: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  useAxiosInterceptor();
  const [selectedCollect, setSelectedCollect] = useState<number[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const timeMinutes = ['00', '15', '30', '45'] as const;
  const timeFullOptions = Array.from({ length: 24 }, (_, i) =>
    timeMinutes.map((m) => `${String(i).padStart(2, '0')}:${m}`)
  ).flat();

  type DeviceSettings = {
    enable_more_lend: boolean;
    enable_quote_client: boolean;
    enable_discount_manual: boolean;
    enable_repay: boolean;
    enable_refinance: boolean;
    mobile_session_enabled: boolean;
    detail_machine: boolean;
    valid_time: boolean;
    time_open_hour: string;
    time_open_minute: string;
    time_close_hour: string;
    time_close_minute: string;
    delete_pay: boolean;
    delete_lend: boolean;
    enable_device_registration: boolean;
    cons_insurance_manual: boolean;
  };

  const [deviceSettingsByCollect, setDeviceSettingsByCollect] = useState<Record<number, DeviceSettings>>({});

  const [office_id, setOffice_id] = useState('');
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const { drawerOpen } = useDrawer();
  const { canUpdate } = useResourcePermissions('/prestamos/actualizarterminal');

  const resetConfigurationState = () => {
    setSelectedCollect([]);
    setCollectsOffice([]);
    setDeviceSettingsByCollect({});
  };

  const parseTimeToParts = (
    value: string | undefined | null,
    fallbackHour: string,
    fallbackMinute: string
  ) => {
    const safeValue = (value ?? '').trim();
    if (!safeValue) {
      return {
        hour: fallbackHour.padStart(2, '0'),
        minute: fallbackMinute.padStart(2, '0'),
      };
    }

    const [hour = fallbackHour, minute = fallbackMinute] = safeValue.split(':');
    return {
      hour: (hour || fallbackHour).trim().padStart(2, '0'),
      minute: (minute || fallbackMinute).trim().padStart(2, '0'),
    };
  };

  const defaultDeviceSettings: DeviceSettings = {
    enable_more_lend: false,
    enable_quote_client: false,
    enable_discount_manual: true,
    enable_repay: false,
    enable_refinance: false,
    mobile_session_enabled: true,
    detail_machine: false,
    valid_time: false,
    time_open_hour: '08',
    time_open_minute: '00',
    time_close_hour: '20',
    time_close_minute: '00',
    delete_pay: false,
    delete_lend: false,
    enable_device_registration: false,
    cons_insurance_manual: false,
  };

  useEffect(() => {
    (async () => {
      try {
        showLoading();
        const { data } = await axios.get('offices');
        setOffices(data.data);
        await new Promise((resolve) => setTimeout(resolve, 2000));
        showAlert('Se ha realizado la consulta exitosamente!', 'info');
      } catch (error) {
        showAlert('Error fetching data!', 'error');
      } finally {
        hideLoading();
      }
    })();
  }, []);

  useEffect(() => {
    resetConfigurationState();
  }, []);

  useEffect(() => {
    if (office_id) {
      resetConfigurationState();

      (async () => {
        showLoading();
        try {
          const { data } = await axios.get(`machinepermission/${office_id}`);
          setCollectsOffice(data);
          const deviceMap: Record<number, DeviceSettings> = {};

          data.forEach((collect: Collect) => {
            const collectId = collect.id as number;

            const openParts = parseTimeToParts(collect.time_open, '08', '00');
            const closeParts = parseTimeToParts(collect.time_close, '20', '00');

            deviceMap[collectId] = {
              enable_more_lend: Boolean(collect.enable_more_lend),
              enable_quote_client: Boolean(Number(collect.enable_quote_client ?? 0)),
              enable_discount_manual: Boolean(Number(collect.enable_discount_manual ?? 1)),
              enable_repay: Boolean(collect.enable_repay),
              enable_refinance: Boolean(collect.enable_refinance),
              mobile_session_enabled:
                Number(collect.user?.mobile_session_enabled ?? 1) === 1,
              detail_machine: Boolean(collect.detail_machine),
              valid_time: Boolean(collect.valid_time),
              time_open_hour: openParts.hour,
              time_open_minute: openParts.minute,
              time_close_hour: closeParts.hour,
              time_close_minute: closeParts.minute,
              delete_pay: Boolean(collect.delete_pay),
              delete_lend: Boolean(collect.delete_lend),
              enable_device_registration: Boolean(collect.enable_device_registration),
              cons_insurance_manual: Boolean(collect.cons_insurance_manual),
            };
          });

          setDeviceSettingsByCollect(deviceMap);

          const selectedIds = data
            .filter((collect: Collect) => {
              const s = deviceMap[collect.id as number];
              return (
                s.enable_more_lend ||
                s.enable_quote_client ||
                s.enable_discount_manual ||
                s.enable_repay ||
                s.enable_refinance ||
                s.mobile_session_enabled ||
                s.detail_machine ||
                s.valid_time ||
                s.delete_pay ||
                s.delete_lend ||
                s.enable_device_registration ||
                s.cons_insurance_manual
              );
            })
            .map((collect: Collect) => Number(collect.id))
            .filter((id: number) => !Number.isNaN(id));

          setSelectedCollect(selectedIds);

          await new Promise((resolve) => setTimeout(resolve, 2000));
          if (data.length > 0) {
            showAlert('Se ha realizado la consulta exitosamente!', 'info');
          } else {
            showAlert('No se encuentran registros!', 'warning');
          }
        } catch (error) {
          showAlert('Error fetching data!', 'error');
        } finally {
          hideLoading();
        }
      })();
      return;
    }

    resetConfigurationState();
  }, [office_id]);
  const handleCheckboxChange = (collectId: number, isActive: boolean) => {
    if (isActive) {
      setSelectedCollect((prevSelected: number[]) => {
        return prevSelected.includes(collectId) ? prevSelected : [...prevSelected, collectId];
      });

      // Al activar el "checkall" del cobro, se activan TODOS los checks de columnas para esa fila.
      setDeviceSettingsByCollect((prev) => ({
        ...prev,
        [collectId]: {
          ...(prev[collectId] ?? defaultDeviceSettings),
          enable_more_lend: true,
          enable_repay: true,
          enable_refinance: true,
          mobile_session_enabled: true,
          detail_machine: true,
          valid_time: true,
          delete_pay: true,
          delete_lend: true,
          enable_device_registration: true,
          cons_insurance_manual: true,
        },
      }));
    } else {
      setSelectedCollect((prevSelected: number[]) => prevSelected.filter(id => id !== collectId));

      // Al desactivar el "checkall" del cobro, se desactivan TODOS los checks de columnas para esa fila.
      setDeviceSettingsByCollect((prev) => ({
        ...prev,
        [collectId]: {
          ...(prev[collectId] ?? defaultDeviceSettings),
          enable_more_lend: false,
          enable_quote_client: false,
          enable_discount_manual: false,
          enable_repay: false,
          enable_refinance: false,
          mobile_session_enabled: false,
          detail_machine: false,
          valid_time: false,
          delete_pay: false,
          delete_lend: false,
          enable_device_registration: false,
          cons_insurance_manual: false,
        },
      }));
    }
  };

  const handleSelectAll = (isActive: boolean) => {
    if (isActive) {
      const allCollectIds = collectsOffice
        .map((collect) => Number(collect.id))
        .filter((id) => !Number.isNaN(id));
      setSelectedCollect(allCollectIds);

      setDeviceSettingsByCollect((prev) => {
        const next = { ...prev };
        allCollectIds.forEach((collectId) => {
          const current = prev[collectId] ?? defaultDeviceSettings;
          next[collectId] = {
            ...current,
            enable_more_lend: true,
            enable_repay: true,
            enable_refinance: true,
            mobile_session_enabled: true,
            detail_machine: true,
            valid_time: true,
            delete_pay: true,
            delete_lend: true,
            enable_device_registration: true,
            cons_insurance_manual: true,
          };
        });
        return next;
      });
    } else {
      setSelectedCollect([]);

      // Si se desactiva el checkall del header, se desactivan TODOS los checks de columnas para todas las filas.
      const allCollectIds = collectsOffice
        .map((collect) => Number(collect.id))
        .filter((id) => !Number.isNaN(id));

      setDeviceSettingsByCollect((prev) => {
        const next = { ...prev };
        allCollectIds.forEach((collectId) => {
          const current = prev[collectId] ?? defaultDeviceSettings;
          next[collectId] = {
            ...current,
            enable_more_lend: false,
            enable_quote_client: false,
            enable_discount_manual: false,
            enable_repay: false,
            enable_refinance: false,
            mobile_session_enabled: false,
            detail_machine: false,
            valid_time: false,
            delete_pay: false,
            delete_lend: false,
            enable_device_registration: false,
            cons_insurance_manual: false,
          };
        });
        return next;
      });
    }
  };

  const handleSave = async () => {
    try {
      if (selectedCollect.length === 0) {
        showAlert('No se han seleccionado cobros para actualizar.', 'warning');
        return;
      }
      showLoading();
      const payload = selectedCollect.map((collectId) => {
        const s = deviceSettingsByCollect[collectId];
        return {
          collect_id: collectId,
          enable_more_lend: s?.enable_more_lend ?? false,
          enable_quote_client: s?.enable_quote_client ?? false,
          enable_discount_manual: s?.enable_discount_manual ?? true,
          enable_repay: s?.enable_repay ?? false,
          enable_refinance: s?.enable_refinance ?? false,
          mobile_session_enabled: s?.mobile_session_enabled ?? true,
          detail_machine: s?.detail_machine ?? false,
          valid_time: s?.valid_time ?? false,
          time_open: s?.valid_time
            ? `${s?.time_open_hour ?? '08'}:${s?.time_open_minute ?? '00'}`
            : null,
          time_close: s?.valid_time
            ? `${s?.time_close_hour ?? '20'}:${s?.time_close_minute ?? '00'}`
            : null,
          delete_pay: s?.delete_pay ?? false,
          delete_lend: s?.delete_lend ?? false,
          enable_device_registration: s?.enable_device_registration ?? false,
          cons_insurance_manual: s?.cons_insurance_manual ?? false,
        };
      });

      const url = "/updatemachine";

      const response = await axios.post(url, payload);

      await new Promise(resolve => setTimeout(resolve, 2000)); // Esperar 2 segundos

      showAlert('Datos guardados exitosamente!', 'success');
    } catch (error) {
      console.error('Error al guardar los datos:', error);
      showAlert('Error al guardar los datos.', 'error');
    } finally {
      hideLoading();
    }
  };

  const emptyDeviceSettings: DeviceSettings = {
    enable_more_lend: false,
    enable_quote_client: false,
    enable_discount_manual: true,
    enable_repay: false,
    enable_refinance: false,
    mobile_session_enabled: true,
    detail_machine: false,
    valid_time: false,
    time_open_hour: '08',
    time_open_minute: '00',
    time_close_hour: '20',
    time_close_minute: '00',
    delete_pay: false,
    delete_lend: false,
    enable_device_registration: false,
    cons_insurance_manual: false,
  };

  type DeviceBooleanKey = Exclude<
    keyof DeviceSettings,
    'time_open_hour' | 'time_open_minute' | 'time_close_hour' | 'time_close_minute'
  >;

  const setCollectDeviceSetting = (
    collectId: number,
    key: DeviceBooleanKey,
    value: boolean
  ) => {
    setDeviceSettingsByCollect((prev) => {
      const current = prev[collectId] ?? emptyDeviceSettings;
      return {
        ...prev,
        [collectId]: {
          ...current,
          [key]: value,
        },
      };
    });

    // Si se interactúa con algún check por cobro, se incluye ese cobro en el payload.
    setSelectedCollect((prev) => (prev.includes(collectId) ? prev : [...prev, collectId]));
  };

  const setCollectTimeSetting = (
    collectId: number,
    key: 'time_open_hour' | 'time_open_minute' | 'time_close_hour' | 'time_close_minute',
    value: string
  ) => {
    setDeviceSettingsByCollect((prev) => {
      const current = prev[collectId] ?? defaultDeviceSettings;
      return {
        ...prev,
        [collectId]: {
          ...current,
          [key]: value,
        },
      };
    });
  };

  const renderSelectAllHeader = () => {
    const allSelected = collectsOffice.length > 0 && selectedCollect.length === collectsOffice.length;
    const someSelected = selectedCollect.length > 0 && selectedCollect.length < collectsOffice.length;

    return (
      <Box display="flex" alignItems="center" justifyContent="center">
        <Checkbox
          indeterminate={someSelected}
          checked={allSelected}
          onChange={(event) => handleSelectAll(event.target.checked)}
        />
      </Box>
    );
  };

  const renderHeaderWithTooltip = (label: string, tooltip: string) => () => (
    <Tooltip title={tooltip} arrow placement='top'>
      <Box
        component='span'
        display='inline-flex'
        alignItems='center'
        justifyContent='center'
        gap={0.5}
        sx={{ cursor: 'help', textAlign: 'center', lineHeight: 1.2 }}
      >
        <span>{label}</span>
        <InfoOutlinedIcon sx={{ fontSize: 14 }} />
      </Box>
    </Tooltip>
  );

  const simpleColumns: SimpleColumn[] = [
    {
      field: 'collect',
      headerName: 'Cobro',
      renderHeader: renderHeaderWithTooltip('Cobro', 'Nombre del cobro o ruta que se está configurando.'),
      flex: 1,
      minWidth: 150,
      wrapText: true,
    },
    {
      field: 'enable_more_lend',
      headerName: 'Prestamos',
      renderHeader: renderHeaderWithTooltip('Prestamos', 'Permite registrar nuevos préstamos desde la app mobile. Si está apagado, solo puede haber un préstamo con saldo por cliente.'),
      align: 'center',
      minWidth: 80,
      maxWidth: 80,
      renderCell: ({ row }: { row: SimpleRow }) => {
        const numericId = typeof row.id === 'number' ? row.id : Number(row.id);
        const checked = Boolean(deviceSettingsByCollect[numericId]?.enable_more_lend);
        return (
          <Checkbox
            checked={checked}
            disabled={!canUpdate}
            onChange={(event) => setCollectDeviceSetting(numericId, 'enable_more_lend', event.target.checked)}
          />
        );
      },
    },
    {
      field: 'enable_quote_client',
      headerName: 'Cupo cliente',
      renderHeader: renderHeaderWithTooltip(
        'Cupo cliente',
        'Si está activo, el tope al crear préstamo es el cupo del cliente; si no, el cupo del cobro.'
      ),
      align: 'center',
      minWidth: 95,
      maxWidth: 95,
      renderCell: ({ row }: { row: SimpleRow }) => {
        const numericId = typeof row.id === 'number' ? row.id : Number(row.id);
        const checked = Boolean(deviceSettingsByCollect[numericId]?.enable_quote_client);
        return (
          <Checkbox
            checked={checked}
            disabled={!canUpdate}
            onChange={(event) => setCollectDeviceSetting(numericId, 'enable_quote_client', event.target.checked)}
          />
        );
      },
    },
    {
      field: 'enable_discount_manual',
      headerName: 'Desc. manual',
      renderHeader: renderHeaderWithTooltip(
        'Desc. manual',
        'Si está activo, en mobile se puede ingresar el descuento manualmente; abono + descuento no puede superar el saldo.'
      ),
      align: 'center',
      minWidth: 95,
      maxWidth: 95,
      renderCell: ({ row }: { row: SimpleRow }) => {
        const numericId = typeof row.id === 'number' ? row.id : Number(row.id);
        const checked = Boolean(deviceSettingsByCollect[numericId]?.enable_discount_manual);
        return (
          <Checkbox
            checked={checked}
            disabled={!canUpdate}
            onChange={(event) =>
              setCollectDeviceSetting(numericId, 'enable_discount_manual', event.target.checked)
            }
          />
        );
      },
    },
    {
      field: 'enable_repay',
      headerName: 'Liquidar',
      renderHeader: renderHeaderWithTooltip('Liquidar', 'Permite realizar el cierre o liquidación del cobro desde el mobile.'),
      align: 'center',
      minWidth: 80,
      maxWidth: 80,
      renderCell: ({ row }: { row: SimpleRow }) => {
        const numericId = typeof row.id === 'number' ? row.id : Number(row.id);
        const checked = Boolean(deviceSettingsByCollect[numericId]?.enable_repay);
        return (
          <Checkbox
            checked={checked}
            disabled={!canUpdate}
            onChange={(event) => setCollectDeviceSetting(numericId, 'enable_repay', event.target.checked)}
          />
        );
      },
    },
    {
      field: 'enable_refinance',
      headerName: 'Refinanciar',
      renderHeader: renderHeaderWithTooltip('Refinanciar', 'Permite refinanciar préstamos desde el mobile.'),
      align: 'center',
      minWidth: 90,
      maxWidth: 90,
      renderCell: ({ row }: { row: SimpleRow }) => {
        const numericId = typeof row.id === 'number' ? row.id : Number(row.id);
        const checked = Boolean(deviceSettingsByCollect[numericId]?.enable_refinance);
        return (
          <Checkbox
            checked={checked}
            disabled={!canUpdate}
            onChange={(event) => setCollectDeviceSetting(numericId, 'enable_refinance', event.target.checked)}
          />
        );
      },
    },
    {
      field: 'mobile_session_enabled',
      headerName: 'Sesión',
      renderHeader: renderHeaderWithTooltip('Sesión', 'Habilita o bloquea completamente el acceso del empleado a la app mobile.'),
      align: 'center',
      minWidth: 80,
      maxWidth: 80,
      renderCell: ({ row }: { row: SimpleRow }) => {
        const numericId = typeof row.id === 'number' ? row.id : Number(row.id);
        const checked = Boolean(deviceSettingsByCollect[numericId]?.mobile_session_enabled);
        return (
          <Checkbox
            checked={checked}
            disabled={!canUpdate}
            onChange={(event) => setCollectDeviceSetting(numericId, 'mobile_session_enabled', event.target.checked)}
          />
        );
      },
    },
    {
      field: 'detail_machine',
      headerName: 'Detalle',
      renderHeader: renderHeaderWithTooltip('Detalle', 'Muestra el detalle ampliado de la terminal o del cobro en la app.'),
      align: 'center',
      minWidth: 80,
      maxWidth: 80,
      renderCell: ({ row }: { row: SimpleRow }) => {
        const numericId = typeof row.id === 'number' ? row.id : Number(row.id);
        const checked = Boolean(deviceSettingsByCollect[numericId]?.detail_machine);
        return (
          <Checkbox
            checked={checked}
            disabled={!canUpdate}
            onChange={(event) => setCollectDeviceSetting(numericId, 'detail_machine', event.target.checked)}
          />
        );
      },
    },
    {
      field: 'delete_pay',
      headerName: 'Eliminar pagos',
      renderHeader: renderHeaderWithTooltip('Eliminar pagos', 'Permite eliminar abonos registrados desde el mobile.'),
      align: 'center',
      minWidth: 80,
      maxWidth: 80,
      renderCell: ({ row }: { row: SimpleRow }) => {
        const numericId = typeof row.id === 'number' ? row.id : Number(row.id);
        const checked = Boolean(deviceSettingsByCollect[numericId]?.delete_pay);
        return (
          <Checkbox
            checked={checked}
            disabled={!canUpdate}
            onChange={(event) => setCollectDeviceSetting(numericId, 'delete_pay', event.target.checked)}
          />
        );
      },
    },
    {
      field: 'delete_lend',
      headerName: 'Eliminar préstamos',
      renderHeader: renderHeaderWithTooltip('Eliminar préstamos', 'Permite eliminar préstamos registrados desde el mobile.'),
      align: 'center',
      minWidth: 80,
      maxWidth: 80,
      renderCell: ({ row }: { row: SimpleRow }) => {
        const numericId = typeof row.id === 'number' ? row.id : Number(row.id);
        const checked = Boolean(deviceSettingsByCollect[numericId]?.delete_lend);
        return (
          <Checkbox
            checked={checked}
            disabled={!canUpdate}
            onChange={(event) => setCollectDeviceSetting(numericId, 'delete_lend', event.target.checked)}
          />
        );
      },
    },
    {
      field: 'enable_device_registration',
      headerName: 'Registro',
      renderHeader: renderHeaderWithTooltip('Registro', 'Exige que el dispositivo esté registrado y autorizado antes de usar la app mobile.'),
      align: 'center',
      minWidth: 80,
      maxWidth: 80,
      renderCell: ({ row }: { row: SimpleRow }) => {
        const numericId = typeof row.id === 'number' ? row.id : Number(row.id);
        const checked = Boolean(deviceSettingsByCollect[numericId]?.enable_device_registration);
        return (
          <Checkbox
            checked={checked}
            disabled={!canUpdate}
            onChange={(event) => setCollectDeviceSetting(numericId, 'enable_device_registration', event.target.checked)}
          />
        );
      },
    },
    {
      field: 'cons_insurance_manual',
      headerName: 'Gestión manual',
      renderHeader: renderHeaderWithTooltip('Gestión manual', 'Define si el gasto de gestión se captura manualmente en vez de calcularse automáticamente en web y mobile.'),
      align: 'center',
      minWidth: 90,
      maxWidth: 90,
      renderCell: ({ row }: { row: SimpleRow }) => {
        const numericId = typeof row.id === 'number' ? row.id : Number(row.id);
        const checked = Boolean(deviceSettingsByCollect[numericId]?.cons_insurance_manual);
        return (
          <Checkbox
            checked={checked}
            disabled={!canUpdate}
            onChange={(event) => setCollectDeviceSetting(numericId, 'cons_insurance_manual', event.target.checked)}
          />
        );
      },
    },
    {
      field: 'valid_time',
      headerName: 'Horarios',
      renderHeader: renderHeaderWithTooltip('Horarios', 'Activa la validación de horario permitido para usar la app mobile.'),
      align: 'center',
      minWidth: 80,
      maxWidth: 80,
      renderCell: ({ row }: { row: SimpleRow }) => {
        const numericId = typeof row.id === 'number' ? row.id : Number(row.id);
        const checked = Boolean(deviceSettingsByCollect[numericId]?.valid_time);
        return (
          <Checkbox
            checked={checked}
            disabled={!canUpdate}
            onChange={(event) => setCollectDeviceSetting(numericId, 'valid_time', event.target.checked)}
          />
        );
      },
    },
    {
      field: 'time_open',
      headerName: 'Hora apertura',
      renderHeader: renderHeaderWithTooltip('Hora apertura', 'Hora desde la cual el cobrador puede usar la app cuando la validación de horarios está activa.'),
      align: 'center',
      minWidth: 105,
      maxWidth: 105,
      renderCell: ({ row }: { row: SimpleRow }) => {
        const numericId = typeof row.id === 'number' ? row.id : Number(row.id);
        const s = deviceSettingsByCollect[numericId];
        return (
          <FormControl size="small" fullWidth>
            <Select
              labelId={`time_open_label_${numericId}`}
              value={`${s?.time_open_hour ?? '08'}:${s?.time_open_minute ?? '00'}`}
              onChange={(event) => {
                const value = String(event.target.value);
                const [hh, mm] = value.split(':');
                setCollectTimeSetting(numericId, 'time_open_hour', hh);
                setCollectTimeSetting(numericId, 'time_open_minute', mm);
              }}
              disabled={!canUpdate || !s?.valid_time}
            >
              {timeFullOptions.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },
    {
      field: 'time_close',
      headerName: 'Hora cierre',
      renderHeader: renderHeaderWithTooltip('Hora cierre', 'Hora límite de uso de la app cuando la validación de horarios está activa.'),
      align: 'center',
      minWidth: 105,
      maxWidth: 105,
      renderCell: ({ row }: { row: SimpleRow }) => {
        const numericId = typeof row.id === 'number' ? row.id : Number(row.id);
        const s = deviceSettingsByCollect[numericId];
        return (
          <FormControl size="small" fullWidth>
            <Select
              labelId={`time_close_label_${numericId}`}
              value={`${s?.time_close_hour ?? '20'}:${s?.time_close_minute ?? '00'}`}
              onChange={(event) => {
                const value = String(event.target.value);
                const [hh, mm] = value.split(':');
                setCollectTimeSetting(numericId, 'time_close_hour', hh);
                setCollectTimeSetting(numericId, 'time_close_minute', mm);
              }}
              disabled={!canUpdate || !s?.valid_time}
            >
              {timeFullOptions.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },
    {
      field: 'select',
      headerName: 'Configurar',
      align: 'center',
      minWidth: 50,
      maxWidth: 50,
      renderHeader: () => (
        <Box display='flex' alignItems='center' justifyContent='center' gap={0.5}>
          {renderSelectAllHeader()}
          <Tooltip title='Selecciona todos los cobros visibles para aplicar la misma configuración.' arrow placement='top'>
            <InfoOutlinedIcon sx={{ fontSize: 14, cursor: 'help' }} />
          </Tooltip>
        </Box>
      ),
      renderCell: ({ row }: { row: SimpleRow }) => {
        const numericId = typeof row.id === 'number' ? row.id : Number(row.id);
        const isChecked = selectedCollect.includes(numericId);
        return (
          <Checkbox
            checked={isChecked}
            disabled={!canUpdate}
            onChange={(event) => handleCheckboxChange(numericId, event.target.checked)}
          />
        );
      },
    }
  ];

  const rows: SimpleRow[] = collectsOffice.map((collect: Collect) => ({
    id: collect.id as RowId,
    admin: collect.user
      ? `${collect.user.first_name ?? ''} ${collect.user.last_name ?? ''}`.trim() || collect.user.username || 'Sin asignar'
      : 'Sin asignar',
    collect: collect.name,
  }));

  return (
    <HeaderResponsive>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Title>Actualizar Terminal Ruta</Title>
        <PermissionButton
          allowed={canUpdate}
          deniedTooltip="Sin permiso para guardar cambios"
          tooltip="Guardar"
          size='small'
          type='submit'
          sx={{ mr: 2 }}
          variant='contained'
          onClick={handleSave} // Llamar a la función handleSave
          startIcon={isMobile ? null : <SaveIcon />}
        >
          {isMobile ? <SaveIcon /> : 'Guardar'}
        </PermissionButton>
      </Box>
      <Card>
        <form onSubmit={e => e.preventDefault()}>
          <CardContent>
            <Grid container columnSpacing={5} rowSpacing={1}>
              <Grid item xs={12} sm={12}>
                <FormControl fullWidth size='small'>
                  <InputLabel id='form-layouts-separator-select-label'>Seleccionar Oficina</InputLabel>
                  {offices && offices.length > 0 ? (
                    <Select
                      label='Seleccionar Oficina'
                      defaultValue=''
                      value={office_id}
                      id='form-layouts-separator-select'
                      labelId='form-layouts-separator-select-label'
                      onChange={(event) => setOffice_id(event.target.value)}
                      required
                    >
                      {offices.map((office: Office) => {
                        return (
                          <MenuItem key={office.id} value={office.id}>
                            {office.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  ) : null}
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>

          <CardContent
            sx={{
              width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto',
            }}
          >
            {collectsOffice && collectsOffice.length > 0 ? (
              <SimpleCustomTable
                rows={rows}
                columns={simpleColumns}
                maxHeight={600}
                showBorders
                showCount
                exportCsv
                exportFileName="actualizar-terminal.csv"
                wrapCellContent
              />
            ) : (
              <EmptyStateMessage message="No existen registros" align="left" />
            )}
          </CardContent>
        </form>
        <Divider sx={{ margin: 0 }} />
        <CardActions>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
            <PermissionButton
              allowed={canUpdate}
              deniedTooltip="Sin permiso para guardar cambios"
              tooltip="Guardar"
              size='small'
              type='submit'
              sx={{ mr: 2 }}
              variant='contained'
              onClick={handleSave} // Llamar a la función handleSave
              startIcon={isMobile ? null : <SaveIcon />}
            >
              {isMobile ? <SaveIcon /> : 'Guardar'}
            </PermissionButton>
          </Box>
        </CardActions>

      </Card >
    </HeaderResponsive >
  );
};

export default ActualizarTerminalAdd;
